<script setup lang="ts">
  // Generic drop-up/down popover. The caller renders its own trigger via the
  // scoped slot. anchor='trigger' panels are positioned `fixed` through
  // @floating-ui/dom (offset + flip + shift) so they never clip inside
  // overflow containers and never leave the viewport; anchor='parent' keeps
  // the legacy CSS-absolute behavior (it intentionally spans the nearest
  // positioned ancestor).
  import { computed, ref } from 'vue';
  import { onClickOutside } from '@vueuse/core';
  import { useFloatingPanel } from './use-floating-panel';

  const props = withDefaults(
    defineProps<{
      placement?: 'top' | 'bottom';
      align?: 'start' | 'end';
      // 'trigger' anchors the panel to the popover root; 'parent' leaves the
      // root static so the panel resolves against the nearest positioned
      // ancestor and spans its full width (needed inside narrow
      // overflow-hidden columns, where a trigger-anchored panel would clip).
      anchor?: 'trigger' | 'parent';
      disabled?: boolean;
      panelClass?: string;
    }>(),
    {
      placement: 'top',
      align: 'start',
      anchor: 'trigger',
      disabled: false,
      panelClass: '',
    }
  );

  const rootRef = ref<HTMLElement>();
  const panelRef = ref<HTMLElement>();
  const open = ref(false);
  // Trigger-anchored panels render at (0,0) until the first position lands;
  // they stay invisible for that first async frame to avoid a flash.
  const { positioned } = useFloatingPanel(
    rootRef,
    panelRef,
    computed(() => open.value && props.anchor === 'trigger'),
    () => `${props.placement}-${props.align}`
  );

  onClickOutside(rootRef, () => {
    open.value = false;
  });

  function toggle() {
    if (props.disabled) return;
    open.value = !open.value;
  }

  function close() {
    open.value = false;
  }
</script>

<template>
  <div
    ref="rootRef"
    class="pos-ui-popover"
    :class="[`anchor-${anchor}`]"
    @keydown.esc="close"
  >
    <slot name="trigger" :open="open" :toggle="toggle" />

    <!-- placement/align classes only matter for the CSS-positioned parent
         anchor; trigger-anchored panels get coordinates from floating-ui. -->
    <div
      v-if="open"
      ref="panelRef"
      class="pos-ui-popover-panel"
      :class="[
        `panel-anchor-${anchor}`,
        anchor === 'parent' ? [`placement-${placement}`, `align-${align}`] : '',
        anchor === 'trigger' && !positioned ? 'is-unpositioned' : '',
        panelClass,
      ]"
    >
      <slot :close="close" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-ui-popover {
    &.anchor-trigger {
      @apply tw-relative tw-inline-flex;
    }

    // Static on purpose: the panel positions against the nearest positioned
    // ancestor. onClickOutside still works — the root DOM-contains both.
    &.anchor-parent {
      @apply tw-static tw-inline-flex;
    }

    .pos-ui-popover-panel {
      @apply tw-z-30 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-surface tw-p-3 tw-shadow-lg;
      // Never taller than the viewport allows: drop-up panels inside
      // overflow-hidden columns hard-clip at the top otherwise. 320px, or
      // 60vh below 533px (= min(60vh, 320px), which old Chrome lacks).
      max-height: 320px;

      @media (max-height: 533.33px) {
        max-height: 60vh;
      }

      // Positioned by floating-ui (fixed strategy): coordinates come from
      // inline styles, so no placement/align margins apply here.
      &.panel-anchor-trigger {
        @apply tw-fixed tw-w-72;
        // = min(92vw, 360px): 92vw below 391px.
        max-width: 360px;

        @media (max-width: 391.3px) {
          max-width: 92vw;
        }

        // Invisible (but measurable) until the first computePosition lands.
        &.is-unpositioned {
          @apply tw-invisible;
        }
      }

      &.panel-anchor-parent {
        @apply tw-absolute tw-left-0 tw-right-0;

        &.placement-top {
          @apply tw-bottom-full tw-mb-1;
        }

        &.placement-bottom {
          @apply tw-top-full tw-mt-1;
        }
      }
    }
  }
</style>
